/**
 * Prop-driven UI primitives shared by the TV and Remote apps, restyled to the
 * "Presence" design system (see styles.css). Still deliberately presentational —
 * they take their state as data, so the transport/orchestration layers are
 * untouched. No business logic here.
 */

import type { ReactNode } from 'react';

export function Screen({ role, children }: { role: 'tv' | 'remote'; children: ReactNode }): JSX.Element {
    return <div className = { `screen screen--${role}` }>{ children }</div>;
}

/** The 8x8 wordmark, optionally followed by a separator + room title. */
export function Logo({ size = '2cqw' }: { size?: string }): JSX.Element {
    return <span className = "logo" style = {{ fontSize: size }}>8&times;8</span>;
}

export function Shell({ children }: { children: ReactNode }): JSX.Element {
    return <div className = "shell">{ children }</div>;
}

export function Card({ children }: { children: ReactNode }): JSX.Element {
    return <div className = "card">{ children }</div>;
}

export function Button(props: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'ghost' | 'danger';
    icon?: ReactNode;
    block?: boolean;
    large?: boolean;
    disabled?: boolean;
    testId?: string;
}): JSX.Element {
    const variant = props.variant ?? 'primary';
    const cls = [
        'btn',
        `btn--${variant}`,
        props.block ? 'btn--block' : '',
        props.large ? 'btn--lg' : ''
    ].filter(Boolean).join(' ');

    return (
        <button className = { cls } disabled = { props.disabled } data-testid = { props.testId } onClick = { props.onClick }>
            { props.icon }
            { props.label }
        </button>
    );
}

export function TextInput(props: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    onEnter?: () => void;
    autoFocus?: boolean;
    testId?: string;
}): JSX.Element {
    return (
        <input
            className = "text-input"
            value = { props.value }
            placeholder = { props.placeholder }
            autoFocus = { props.autoFocus }
            data-testid = { props.testId }
            onChange = { e => props.onChange(e.target.value) }
            onKeyDown = { e => {
                if (e.key === 'Enter') {
                    props.onEnter?.();
                }
            } } />
    );
}

/**
 * Segmented code field: `length` cells overlaid on one transparent input, so it
 * behaves like a normal text box (paste, autofill, Enter) but looks like the
 * design's per-character cells. WYSIWYG: the value is shown and emitted EXACTLY as
 * typed — what the TV renders (lowercase share keys / setup codes) must be what gets
 * sent, so the field must NOT change case (no `toUpperCase`, no auto-capitalize).
 * Forcing uppercase here is what broke pairing on the re-skin. See the cells'
 * `text-transform: none` in styles.css; pairing also normalizes case server-side.
 */
export function CodeInput(props: {
    value: string;
    length: number;
    onChange: (value: string) => void;
    onEnter?: () => void;
    autoFocus?: boolean;
    testId?: string;
}): JSX.Element {
    const chars = props.value.slice(0, props.length).split('');
    const active = Math.min(chars.length, props.length - 1);

    return (
        <div className = "codefield">
            <div className = "codefield__cells">
                { Array.from({ length: props.length }, (_, i) => (
                    <div key = { i } className = { `codefield__cell${i === active && chars.length < props.length ? ' codefield__cell--active' : ''}` }>
                        { chars[i] ?? '' }
                    </div>
                )) }
            </div>
            <input
                className = "codefield__input"
                value = { props.value }
                maxLength = { props.length }
                autoFocus = { props.autoFocus }
                inputMode = "text"
                autoCapitalize = "none"
                autoCorrect = "off"
                spellCheck = { false }
                autoComplete = "one-time-code"
                data-testid = { props.testId }
                onChange = { e => props.onChange(e.target.value) }
                onKeyDown = { e => {
                    if (e.key === 'Enter') {
                        props.onEnter?.();
                    }
                } } />
        </div>
    );
}

export function StatusLine({ tone, text }: { tone: 'ok' | 'warn' | 'err' | 'info'; text: string }): JSX.Element {
    return <p className = { `status status--${tone}` } data-testid = "status-line">{ text }</p>;
}

export function Dot({ kind }: { kind: 'live' | 'blue' | 'idle' | 'rec' }): JSX.Element {
    return <span className = { `dot dot--${kind}` } />;
}

export function Pill(props: {
    label: string;
    variant: 'avail' | 'on' | 'off' | 'host' | 'rec' | 'soon';
    dot?: boolean;
    testId?: string;
}): JSX.Element {
    return (
        <span className = { `pill pill--${props.variant}` } data-testid = { props.testId }>
            { props.dot ? <span className = "dot" /> : null }
            { props.label }
        </span>
    );
}

const AVATAR_COLORS = ['#4687ed', '#c6dc60', '#e0598a', '#9b59b6', '#2db7b0', '#dd7011', '#3ddd86', '#5b7cfa'];

/** Deterministic avatar colour from a seed string. */
export function avatarColor(seed: string): string {
    let h = 0;

    for (let i = 0; i < seed.length; i++) {
        h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    }

    return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

export function Avatar({ label, seed }: { label: string; seed: string }): JSX.Element {
    return (
        <span className = "occ__av" style = {{ background: avatarColor(seed) }}>
            { label.slice(0, 2).toUpperCase() }
        </span>
    );
}
